/**
 * Stay display shared by the full search, the compact panel and the sheet / popover: the dates shown on a trigger
 * button and in the dates header, the booking page URL. Light: no calendar, no cards. (No stay-check note since
 * 0.9.4: the results block explains, with its summary and suggestions.)
 */
import { h } from './dom.js';
import { nightsBetween } from './dates.js';

/** Dates as shown on a trigger button: « Du 9 mars au 12 mars 2027 · 3 nuits », or the placeholder. */
export function datesLabel(ctx) {
	const s = ctx.booking.state;
	if (s.arrival && s.departure) {
		return ctx.msg.stayRange(s.arrival, s.departure, nightsBetween(s.arrival, s.departure));
	}
	if (s.arrival) {
		return ctx.i18n.t('ui.datesArrival', [ctx.i18n.date(s.arrival, 'short')]);
	}
	return ctx.i18n.t('ui.datesButton');
}

/**
 * Summary line of the dates section header (date-picker change, §7): « Choisissez vos dates d'arrivée et de
 * départ », « Arrivée le 20 oct. · choisissez le départ », « 20 oct. – 23 oct. · 3 nuits » (short Intl dates).
 * short = the search bar's Dates tab (0.9.9): « Choisissez vos dates » when nothing is chosen.
 */
export function datesSummary(ctx, { short = false } = {}) {
	const { booking, i18n } = ctx;
	const s = booking.state;
	const d = (iso) => i18n.date(iso, 'dayMonth');
	if (s.arrival && s.departure) {
		const n = nightsBetween(s.arrival, s.departure);
		return i18n.t('ui.datesRange', [d(s.arrival), d(s.departure), n], n);
	}
	if (s.arrival) {
		return i18n.t('ui.datesArrival', [d(s.arrival)]);
	}
	return i18n.t(short ? 'ui.datesPromptShort' : 'ui.datesPrompt');
}

/**
 * Phone Dates tab of the search bar (0.9.9): the range on the first line (« 17–25 mars », « 29 mars – 5 avr. »)
 * and the nights on a second (« 8 nuits »); arrival only: « 17 mars → »; nothing chosen: « Dates ».
 */
export function datesShort(ctx) {
	const { booking, i18n } = ctx;
	const s = booking.state;
	if (s.arrival && s.departure) {
		const n = nightsBetween(s.arrival, s.departure);
		return [i18n.value(i18n.dateRange(s.arrival, s.departure)), h('em', {}, i18n.t('ui.nights', [n], n))];
	}
	if (s.arrival) {
		return [i18n.value(i18n.date(s.arrival, 'dayMonth')), i18n.sep(' →')];
	}
	return [i18n.t('ui.datesTitle')];
}

/**
 * The booking page URL with the guest's choices (CHANGE 2026-10-18 §1.3): arrivee, depart, adultes, enfants,
 * room_id (the Beds24 room ID; not `chambre`, a WordPress query variable here: 1.6.2) — read back by
 * booking.applyParams().
 */
export function bookingUrl(target, s, room = null) {
	const url = new URL(target || window.location.href, window.location.href);
	url.searchParams.set('arrivee', s.arrival);
	url.searchParams.set('depart', s.departure);
	url.searchParams.set('adultes', String(s.adults));
	url.searchParams.set('enfants', String(s.childrenAges.length));
	if (room) {
		url.searchParams.set('room_id', String(room));
	}
	return url.toString();
}
